import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Axios from 'axios';
import {Helmet} from 'react-helmet-async';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useContext, useEffect, useRef, useState} from 'react';
import {Store} from '../Store';
import {toast} from 'react-toastify';
import Header from '../icon/components/global/header/Header';
import Footer from '../icon/components/global/footer/Footer';
import {api} from '../Api';
import {getError, otpProviderLabel} from '../utils';
import SecurityCeremony from '../component/SecurityCeremony';
import SecurityBadges from '../component/SecurityBadges';

// A login code is good for two minutes on screen.
const LOGIN_WINDOW_SECONDS = 120;

const mmss = (total) => {
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// Say where the code really went. Until the follow-up ask comes back we can
// only repeat what we asked the gateway for, which is not the same thing.
const routeLabel = (real, otp) => {
  const over = String(real?.deliveredOver || '').toLowerCase();
  if (over === 'whatsapp') return ` on WhatsApp${real?.sender ? ` from ${real.sender}` : ''}`;
  if (over === 'sms') return ' by SMS';
  // Deliberately silent until the gateway has been asked. Naming a channel we
  // only requested is how the screen ended up promising an SMS that had in
  // fact gone out over WhatsApp.
  return '';
};

export default function SigningScreen() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const redirectInUrl = new URLSearchParams(search).get('redirect');
  const redirect = redirectInUrl ? redirectInUrl : '/user-panel';
  const redirectUrl = redirect && redirect.startsWith('/') ? redirect : '/';

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Which security ceremony is on screen: null, 'dispatch' or 'verify'. Kept
  // apart from `submitting` because the overlay is what actually stops a second
  // submit — a disabled button still leaves the form live to the Enter key.
  const [ceremony, setCeremony] = useState(null);

  // OTP step state
  const [otp, setOtp] = useState(null); // { otpToken, maskedPhone, allowPassword, ... }
  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  // Seconds left on the 2-minute window the member has to use this code.
  const [expiryLeft, setExpiryLeft] = useState(0);
  // Which road the code actually took. The send answer cannot say — the gateway
  // may accept it as an SMS and then carry it over WhatsApp — so this is filled
  // in from a follow-up ask a few seconds later.
  const [realRoute, setRealRoute] = useState(null);
  const timerRef = useRef(null);

  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;

  const startCountdown = (seconds) => {
    setCountdown(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };
  useEffect(() => () => clearInterval(timerRef.current), []);

  const finishLogin = (data) => {
    ctxDispatch({ type: 'USER_SIGNIN', payload: data });
    localStorage.setItem('userInfo', JSON.stringify(data));
  };

  // The 2-minute window on the code itself, separate from the resend cooldown.
  useEffect(() => {
    if (!otp) return undefined;
    setExpiryLeft(LOGIN_WINDOW_SECONDS);
    const t = setInterval(() => setExpiryLeft((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [otp?.otpToken, otp?.issuedAt]);

  // routePoll: ask a few times what actually carried the code, then stop.
  useEffect(() => {
    if (!otp?.otpToken) return undefined;
    let tries = 0;
    let stopped = false;
    // routePollTuned: the gateway is asked at 5s, so start after that.
    const ask = async () => {
      tries += 1;
      try {
        const { data } = await Axios.post(api + '/api/users/login-otp-route', { otpToken: otp.otpToken });
        if (stopped) return;
        if (data?.deliveredOver) setRealRoute(data);
      } catch (err) {
        /* the banner just keeps its first guess */
      }
      if (!stopped && tries < 6) setTimeout(ask, 5000);
    };
    const first = setTimeout(ask, 7000);
    return () => { stopped = true; clearTimeout(first); };
  }, [otp?.otpToken]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setCeremony('dispatch');
    try {
      const { data } = await Axios.post(api + '/api/users/signin', { phone, password });
      if (data.otpRequired) {
        setOtp({ ...data, issuedAt: Date.now() });
        setCode('');
        setRealRoute(null);
        startCountdown(data.resendAfterSeconds || 60);
        toast.info(`OTP sent to ${data.maskedPhone || 'your number'}.`);
      } else {
        finishLogin(data);
      }
    } catch (err) {
      toast.error(getError(err) || 'Unable to Login right now. Please try again later.');
    } finally {
      setSubmitting(false);
      setCeremony(null);
    }
  };

  const verifyHandler = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setCeremony('verify');
    try {
      const { data } = await Axios.post(api + '/api/users/verify-login-otp', {
        otpToken: otp.otpToken,
        code,
      });
      finishLogin(data);
    } catch (err) {
      toast.error(getError(err) || 'Incorrect code.');
    } finally {
      setSubmitting(false);
      setCeremony(null);
    }
  };

  const resendHandler = async () => {
    if (countdown > 0 || submitting) return;
    setSubmitting(true);
    setCeremony('dispatch');
    try {
      const { data } = await Axios.post(api + '/api/users/resend-login-otp', {
        otpToken: otp.otpToken,
      });
      setOtp((current) => ({
        ...current,
        maskedPhone: data.maskedPhone || current.maskedPhone,
        sentVia: data.sentVia || current.sentVia,
        smsFollowUpSeconds: data.smsFollowUpSeconds ?? current.smsFollowUpSeconds,
        issuedAt: Date.now(),
      }));
      setRealRoute(null);
      startCountdown(data.resendAfterSeconds || 60);
      toast.success('A new code was sent.');
    } catch (err) {
      toast.error(getError(err) || 'Could not resend the code.');
    } finally {
      setSubmitting(false);
      setCeremony(null);
    }
  };

  useEffect(() => {
    if (userInfo) {
      // Three destinations: admin portal, the single staff portal (agents,
      // user managers and product managers all land here), or the user panel.
      const isStaff =
        userInfo.isAgent || userInfo.isUserManager || userInfo.isProductManager;
      if (userInfo.isAdmin) navigate('/admin/dashboard');
      else if (isStaff) navigate('/agent/dashboard');
      else navigate(redirectUrl);
    }
  }, [navigate, redirectUrl, userInfo]);

  return (
    <>
      <div>
        <Helmet><title>Sign In </title></Helmet>
        <SecurityCeremony
          active={!!ceremony}
          flow={ceremony || 'dispatch'}
          title={ceremony === 'verify' ? 'Verifying your code' : 'Sending your secure code'}
        />
        <Header/>
        <Container className="small-container">
          {!otp ? (
            <>
              <h1 className="my-3 text-center fp">Please Sign In</h1>
              <Form onSubmit={submitHandler}>
                <Form.Group className="mb-3" controlId="phone">
                  <Form.Label>Username</Form.Label>
                  <Form.Control type="text" onChange={(e) => setPhone(e.target.value)} required />
                </Form.Group>
                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>Password</Form.Label>
                  <Form.Control type="password" onChange={(e) => setPassword(e.target.value)} required />
                </Form.Group>
                <div className="mb-3 ">
                  <Button type="submit" variant="outline-success w-100" disabled={submitting}>
                    {submitting ? 'Please wait…' : 'Sign in'}
                  </Button>
                  <div className="mb-3" style={{ display: 'none' }}>
                    {' '}No Account?{' '}
                    <Link to={`/signup?redirect=${redirect}`}>Create a New Account</Link>
                  </div>
                </div>
              </Form>
              <div className="mb-3 text-center">
                <Link to="/download-app">
                  <i className="fa-brands fa-android" aria-hidden="true" style={{ marginRight: 6 }}></i>
                  Get the Android app
                </Link>
              </div>
              <SecurityBadges variant="login" />
            </>
          ) : (
            <>
              <h1 className="my-3 text-center fp">Enter Verification Code</h1>
              <p className="text-center text-muted">
                We sent a 6-digit code to <strong>{otp.maskedPhone}</strong>
                {routeLabel(realRoute, otp)}.
                {expiryLeft > 0 && (
                  <>
                    <br />
                    <span>This code is good for <strong>{mmss(expiryLeft)}</strong>.</span>
                  </>
                )}
                {!realRoute?.smsFollowUpSent && otp.smsFollowUpSeconds > 0 && (
                  <>
                    <br />
                    <small>Nothing arrived? We will text it to you automatically.</small>
                  </>
                )}
                {realRoute?.smsFollowUpSent && (
                  <>
                    <br />
                    <small>We have also sent it by SMS.</small>
                  </>
                )}
              </p>
              <Form onSubmit={verifyHandler}>
                <Form.Group className="mb-3" controlId="code">
                  <Form.Label>{otp.allowPassword ? 'OTP or Password' : 'OTP Code'}</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder={otp.allowPassword ? 'Enter the code or your password' : 'Enter the 6-digit code'}
                    required
                  />
                  {otp.allowPassword && (
                    <Form.Text className="text-muted">You can also type your account password here.</Form.Text>
                  )}
                </Form.Group>
                <Button type="submit" variant="outline-success w-100" disabled={submitting}>
                  {submitting ? 'Verifying…' : 'Verify & Sign in'}
                </Button>
              </Form>
              <div className="text-center mt-3">
                {countdown > 0 ? (
                  <span className="text-muted">Resend code in {countdown}s</span>
                ) : (
                  <Button variant="link" onClick={resendHandler} disabled={submitting}>
                    Resend code
                  </Button>
                )}
                <div className="mt-2">
                  <Button variant="link" className="text-muted" onClick={() => { setOtp(null); setCode(''); }}>
                    ← Back to login
                  </Button>
                </div>
              </div>
              <SecurityBadges variant="login" />
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  );
}
